// =============================================================================
// THE API CONTRACT (FROZEN — Phase 0)
// =============================================================================
// Shared shape for `POST /api/contact`, imported by BOTH the BFF and the SPA.
//
// IMPORT-SAFETY: this file must stay node-free AND browser-free. No `node:*`
// imports, no DOM imports, no env reads — only `zod`, which both sides depend on.
// Both tsconfigs reference `../shared/*` (see README "Shared contract"). Do not
// add side effects here.
//
// Field bounds intentionally mirror the Postgres CHECK constraints in
// migrations/0001_contact.sql (zod is the primary gate; the CHECKs are a backstop).
// If you change a bound here, change it there too.
// =============================================================================
import { z } from "zod";

export const LOCALES = ["zh", "en"] as const;
export type Locale = (typeof LOCALES)[number];

/** Request body for POST /api/contact. */
export const contactRequestSchema = z
  .object({
    name: z.string().trim().min(1).max(100),
    company: z.string().trim().max(200).optional(),
    email: z.string().trim().email().min(3).max(254),
    phone: z.string().trim().max(40).optional(),
    subject: z.string().trim().max(200).optional(),
    message: z.string().trim().min(1).max(4000),
    locale: z.enum(LOCALES),

    // --- anti-spam fields (validated server-side; see plan §7.4) ---
    // Honeypot: a hidden field a human never sees. It is NOT validated for
    // emptiness here on purpose — a non-empty value is a bot, but failing schema
    // validation would return a 400 whose message names `website`, advertising the
    // trap. Instead we accept any short string and let the BFF's honeypot check
    // (antispam.ts) handle a filled value with a silent fake-success (see §7.4).
    // The generous max only guards against an oversized junk payload.
    website: z.string().max(200).optional().default(""),
    // Client render timestamp (ms epoch) for the time-trap. The BFF rejects
    // submissions that are too fast (<2s) or stale (>2h).
    renderedAt: z.number().int().nonnegative(),
    // Cloudflare Turnstile response token (optional when Turnstile is disabled).
    turnstileToken: z.string().optional(),
  })
  .strict();

export type ContactRequest = z.infer<typeof contactRequestSchema>;

// --- Response envelopes (mirror realestate's `{ error: { code, message, details } }`) ---

export interface ContactSuccess {
  ok: true;
}

export interface ContactError {
  error: {
    code: ContactErrorCode;
    message: string;
    details?: Record<string, unknown>;
  };
}

export type ContactResponse = ContactSuccess | ContactError;

/** Canonical error codes returned by POST /api/contact. */
export type ContactErrorCode =
  | "CSRF" // missing/invalid X-Requested-With
  | "VALIDATION" // body failed zod
  | "ANTISPAM" // honeypot / time-trap / Turnstile
  | "DAILY_LIMIT" // global Redis cap hit
  | "PAYLOAD_TOO_LARGE" // body > 16KB
  | "LENGTH_REQUIRED" // chunked transfer on /api/*
  | "UNAVAILABLE" // DB/Redis down
  | "INTERNAL"; // unexpected

/** Header used as the CSRF gate. SPA sets it; BFF requires it on /api/*. */
export const XRW_HEADER = "X-Requested-With";
export const XRW_VALUE = "zjl-web";
